import React, { useState, useRef } from "react";
import {
  ChevronRight, ChevronLeft, CheckCircle, FileText, Shield,
  Upload, X, Plus, AlertCircle, Clock, Eye, Edit3, MessageSquare,
  Paperclip, HelpCircle, Check, ArrowLeft, ExternalLink
} from "lucide-react";
import { detectLocale } from "../i18n";

const wData = {
  en: {
    overline: "WARRANTY",
    title: "Open warranty request",
    subtitle: "It takes just a few minutes to start your request. Have your serial number, proof of purchase, and photos or video of the issue ready.",
    helpText: "Before continuing, make sure you have the basic product and purchase details. This helps speed up the analysis.",
    checklist: ["Serial number", "Proof of purchase", "Photos or video of the issue", "Contact details"],
    ctaPrimary: "Continue",
    ctaSecondary: "View warranty terms",
    stepLabel: "Step", of: "of", back: "Back", next: "Continue", submit: "Submit request",
    steps: ["Check", "Product", "Contact", "Issue", "Upload", "Review", "Done"],
    // Step 1
    s1_title: "Initial check",
    s1_text: "Let's confirm the basic information to proceed with your request.",
    s1_q1: "Was the product purchased from SharkX or an authorized channel?",
    s1_q1_a1: "Yes", s1_q1_a2: "Not sure",
    s1_q2: "Is the item still within the coverage period?",
    s1_q2_a1: "Yes", s1_q2_a2: "Not sure",
    s1_q3: "Does the issue seem covered by warranty?",
    s1_q3_a1: "Yes", s1_q3_a2: "Not sure",
    s1_manual: "Your case will proceed to manual review.",
    // Step 2
    s2_title: "Product identification",
    s2_text: "Provide the product details so we can identify the correct coverage.",
    s2_type: "Product type",
    s2_type_ph: "Select type",
    s2_type_opts: ["E-Foil", "Jetboard", "Accessory", "Battery", "Other"],
    s2_model: "Product model", s2_model_ph: "e.g. SharkX Pro",
    s2_serial: "Serial number", s2_serial_ph: "e.g. SX-2025-001234",
    s2_variant: "Variant / configuration", s2_variant_ph: "e.g. Full Carbon",
    s2_purchase_date: "Purchase date", s2_purchase_loc: "Purchase location", s2_purchase_loc_ph: "e.g. SharkX official store",
    s2_help: "The serial number helps speed up the analysis.",
    // Step 3
    s3_title: "Order details",
    s3_text: "We need this information to locate your purchase and respond quickly.",
    s3_name: "Full name", s3_name_ph: "Your full name",
    s3_email: "Email", s3_email_ph: "your@email.com",
    s3_phone: "Phone / WhatsApp", s3_phone_ph: "+1 555 000 0000",
    s3_order: "Order number", s3_order_ph: "Optional",
    s3_country: "Country / Region", s3_country_ph: "e.g. Brazil",
    s3_city: "City", s3_city_ph: "e.g. São Paulo",
    s3_channel: "Purchase channel",
    s3_channel_opts: ["Website", "Store", "Reseller", "Other"],
    s3_help: "If you don't have the order number, you can still continue and attach the receipt later.",
    // Step 4
    s4_title: "Describe the issue",
    s4_text: "Explain what happened as objectively as possible.",
    s4_category: "Issue category",
    s4_cat_opts: ["Won't turn on", "Battery", "Remote / controller", "Structure", "Charger", "Noise", "Vibration", "Physical damage", "Other"],
    s4_desc: "Describe what happened", s4_desc_ph: "Be as specific as possible...",
    s4_when: "When did the issue start?",
    s4_context: "When did it happen?",
    s4_ctx_opts: ["During use", "Transport", "Storage", "Charging", "Other"],
    s4_partial: "Does the product still work partially?",
    s4_partial_yes: "Yes, partially", s4_partial_no: "No", s4_partial_na: "Not sure",
    s4_frequency: "Is the issue intermittent or constant?",
    s4_freq_int: "Intermittent", s4_freq_const: "Constant",
    s4_example1: "Example: the remote won't connect after the last charge.",
    s4_example2: "Example: the battery won't charge and the LED keeps blinking.",
    // Step 5
    s5_title: "Attach evidence",
    s5_text: "Images and videos help speed up the evaluation.",
    s5_product: "Product photo",
    s5_serial: "Serial number photo",
    s5_defect: "Defect photo",
    s5_video: "Issue video",
    s5_receipt: "Proof of purchase",
    s5_other: "Other relevant files",
    s5_formats: "Accepted: JPG, PNG, MP4, PDF. Max 20 MB per file.",
    s5_help: "Send clear images of the defect, serial number, and proof of purchase.",
    s5_drop: "Click or drag files here",
    s5_uploading: "Uploading...",
    s5_remove: "Remove",
    // Step 6
    s6_title: "Review before sending",
    s6_text: "Check the information before finalizing. After submission, you will receive a protocol number to track your case.",
    s6_product: "Product", s6_serial: "Serial number", s6_contact: "Contact",
    s6_issue: "Issue description", s6_attachments: "Attachments", s6_notes: "Notes",
    s6_terms: "I declare that the information provided is true and I agree with the applicable warranty and privacy policy.",
    s6_edit: "Edit", s6_send: "Submit request",
    // Step 7
    s7_title: "Request submitted successfully",
    s7_text: "Your request has been created. Save this protocol number to track your case.",
    s7_protocol: "Protocol number",
    s7_status: "Current status", s7_status_val: "Received",
    s7_summary: "Request summary",
    s7_eta: "Estimated response time", s7_eta_val: "2-5 business days",
    s7_channel: "Tracking channel",
    s7_view: "View request", s7_support: "Back to support", s7_faq: "Explore FAQ",
    s7_secondary: "Our team will review the information and respond through the provided channel.",
    // Tracking
    t_title: "Warranty request tracking",
    t_not_found: "Request not found. Check the URL or contact support.",
    t_timeline: "Timeline", t_messages: "Messages", t_documents: "Pending documents",
    t_eta: "Estimated response", t_updated: "Last update",
    t_status_opts: ["Received", "Under review", "Awaiting documents", "Technical evaluation", "Approved", "Rejected", "Completed", "Cancelled"],
    t_add_doc: "Send additional document", t_reply: "Reply to ticket", t_talk: "Contact support",
    t_support_msg: "If any information is missing, you can supplement the request from this screen.",
  },
  pt: {
    overline: "GARANTIA",
    title: "Abrir solicitação de garantia",
    subtitle: "Leva poucos minutos para iniciar sua solicitação. Tenha seu número de série, comprovante de compra e fotos ou vídeo do problema em mãos.",
    helpText: "Antes de continuar, confirme se você tem os dados básicos do produto e da compra. Isso ajuda a acelerar a análise.",
    checklist: ["Número de série", "Comprovante de compra", "Fotos ou vídeo do problema", "Dados de contato"],
    ctaPrimary: "Continuar",
    ctaSecondary: "Ver termos de garantia",
    stepLabel: "Etapa", of: "de", back: "Voltar", next: "Continuar", submit: "Enviar solicitação",
    steps: ["Verificar", "Produto", "Contato", "Problema", "Anexos", "Revisão", "Enviado"],
    s1_title: "Verificação inicial",
    s1_text: "Vamos confirmar se temos as informações básicas para seguir com sua solicitação.",
    s1_q1: "O produto foi comprado na SharkX ou em canal autorizado?",
    s1_q1_a1: "Sim", s1_q1_a2: "Não sei",
    s1_q2: "O item ainda está dentro do período de cobertura?",
    s1_q2_a1: "Sim", s1_q2_a2: "Não sei",
    s1_q3: "O problema parece coberto pela garantia?",
    s1_q3_a1: "Sim", s1_q3_a2: "Não sei",
    s1_manual: "Seu caso seguirá para análise manual.",
    s2_title: "Identificação do produto",
    s2_text: "Informe o produto para que possamos identificar a cobertura correta.",
    s2_type: "Tipo de produto",
    s2_type_ph: "Selecione o tipo",
    s2_type_opts: ["E-Foil", "Jetboard", "Acessório", "Bateria", "Outro"],
    s2_model: "Modelo do produto", s2_model_ph: "Ex: SharkX Pro",
    s2_serial: "Número de série", s2_serial_ph: "Ex: SX-2025-001234",
    s2_variant: "Variante / configuração", s2_variant_ph: "Ex: Full Carbon",
    s2_purchase_date: "Data da compra", s2_purchase_loc: "Local de compra", s2_purchase_loc_ph: "Ex: Loja oficial SharkX",
    s2_help: "O número de série ajuda a acelerar a análise.",
    s3_title: "Dados do pedido",
    s3_text: "Precisamos destes dados para localizar sua compra e retornar rapidamente.",
    s3_name: "Nome completo", s3_name_ph: "Seu nome completo",
    s3_email: "E-mail", s3_email_ph: "seu@email.com",
    s3_phone: "Telefone / WhatsApp", s3_phone_ph: "+55 11 99999-0000",
    s3_order: "Número do pedido", s3_order_ph: "Opcional",
    s3_country: "País / Região", s3_country_ph: "Ex: Brasil",
    s3_city: "Cidade", s3_city_ph: "Ex: São Paulo",
    s3_channel: "Forma de compra",
    s3_channel_opts: ["Site", "Loja", "Revendedor", "Outro"],
    s3_help: "Se você não tiver o número do pedido, ainda pode continuar e anexar o comprovante depois.",
    s4_title: "Descreva o problema",
    s4_text: "Explique o que aconteceu da forma mais objetiva possível.",
    s4_category: "Categoria do problema",
    s4_cat_opts: ["Não liga", "Bateria", "Controle", "Estrutura", "Carregador", "Ruído", "Vibração", "Dano físico", "Outro"],
    s4_desc: "Descreva o que aconteceu", s4_desc_ph: "Seja o mais específico possível...",
    s4_when: "Quando o problema começou?",
    s4_context: "Aconteceu durante?",
    s4_ctx_opts: ["Uso", "Transporte", "Armazenamento", "Carregamento", "Outro"],
    s4_partial: "O produto ainda funciona parcialmente?",
    s4_partial_yes: "Sim, parcialmente", s4_partial_no: "Não", s4_partial_na: "Não sei",
    s4_frequency: "O problema é intermitente ou constante?",
    s4_freq_int: "Intermitente", s4_freq_const: "Constante",
    s4_example1: "Exemplo: o controle não conecta após a última carga.",
    s4_example2: "Exemplo: a bateria não carrega e o LED fica piscando.",
    s5_title: "Anexe evidências",
    s5_text: "As imagens e vídeos ajudam a acelerar a avaliação.",
    s5_product: "Foto do produto",
    s5_serial: "Foto do número de série",
    s5_defect: "Foto do defeito",
    s5_video: "Vídeo do problema",
    s5_receipt: "Comprovante de compra",
    s5_other: "Outros arquivos relevantes",
    s5_formats: "Aceitos: JPG, PNG, MP4, PDF. Máx 20 MB por arquivo.",
    s5_help: "Envie imagens nítidas do defeito, do número de série e do comprovante de compra.",
    s5_drop: "Clique ou arraste arquivos aqui",
    s5_uploading: "Enviando...",
    s5_remove: "Remover",
    s6_title: "Revise antes de enviar",
    s6_text: "Confira as informações antes de finalizar. Depois do envio, você receberá um número de protocolo para acompanhar o caso.",
    s6_product: "Produto", s6_serial: "Número de série", s6_contact: "Contato",
    s6_issue: "Descrição do problema", s6_attachments: "Anexos", s6_notes: "Observações",
    s6_terms: "Declaro que as informações enviadas são verdadeiras e concordo com a política de garantia e privacidade aplicável.",
    s6_edit: "Editar", s6_send: "Enviar solicitação",
    s7_title: "Solicitação enviada com sucesso",
    s7_text: "Sua solicitação foi criada. Guarde este número de protocolo para acompanhar o atendimento.",
    s7_protocol: "Número do protocolo",
    s7_status: "Status atual", s7_status_val: "Recebido",
    s7_summary: "Resumo do pedido",
    s7_eta: "Prazo estimado de retorno", s7_eta_val: "2-5 dias úteis",
    s7_channel: "Canal de acompanhamento",
    s7_view: "Ver solicitação", s7_support: "Voltar para suporte", s7_faq: "Explorar FAQ",
    s7_secondary: "Nossa equipe vai analisar as informações e retornar pelo canal informado.",
    t_title: "Acompanhamento de solicitação de garantia",
    t_not_found: "Solicitação não encontrada. Verifique a URL ou entre em contato com o suporte.",
    t_timeline: "Linha do tempo", t_messages: "Mensagens", t_documents: "Documentos pendentes",
    t_eta: "Prazo estimado", t_updated: "Última atualização",
    t_status_opts: ["Recebido", "Em análise", "Aguardando documentos", "Em avaliação técnica", "Aprovado", "Rejeitado", "Concluído", "Cancelado"],
    t_add_doc: "Enviar documento adicional", t_reply: "Responder ao chamado", t_talk: "Falar com suporte",
    t_support_msg: "Se faltar alguma informação, você poderá complementar o chamado por esta tela.",
  },
  es: {
    overline: "GARANTÍA",
    title: "Abrir solicitud de garantía",
    subtitle: "Toma solo unos minutos iniciar su solicitud. Tenga su número de serie, comprobante de compra y fotos o video del problema listos.",
    helpText: "Antes de continuar, asegúrese de tener los datos básicos del producto y la compra. Esto ayuda a acelerar el análisis.",
    checklist: ["Número de serie", "Comprobante de compra", "Fotos o video del problema", "Datos de contacto"],
    ctaPrimary: "Continuar",
    ctaSecondary: "Ver términos de garantía",
    stepLabel: "Paso", of: "de", back: "Volver", next: "Continuar", submit: "Enviar solicitud",
    steps: ["Verificar", "Producto", "Contacto", "Problema", "Adjuntos", "Revisión", "Enviado"],
    s1_title: "Verificación inicial",
    s1_text: "Vamos a confirmar si tenemos la información básica para seguir con su solicitud.",
    s1_q1: "¿El producto fue comprado en SharkX o en un canal autorizado?",
    s1_q1_a1: "Sí", s1_q1_a2: "No estoy seguro",
    s1_q2: "¿El artículo aún está dentro del período de cobertura?",
    s1_q2_a1: "Sí", s1_q2_a2: "No estoy seguro",
    s1_q3: "¿El problema parece cubierto por la garantía?",
    s1_q3_a1: "Sí", s1_q3_a2: "No estoy seguro",
    s1_manual: "Su caso seguirá para análisis manual.",
    s2_title: "Identificación del producto",
    s2_text: "Informe el producto para que podamos identificar la cobertura correcta.",
    s2_type: "Tipo de producto",
    s2_type_ph: "Seleccione el tipo",
    s2_type_opts: ["E-Foil", "Jetboard", "Accesorio", "Batería", "Otro"],
    s2_model: "Modelo del producto", s2_model_ph: "Ej: SharkX Pro",
    s2_serial: "Número de serie", s2_serial_ph: "Ej: SX-2025-001234",
    s2_variant: "Variante / configuración", s2_variant_ph: "Ej: Full Carbon",
    s2_purchase_date: "Fecha de compra", s2_purchase_loc: "Lugar de compra", s2_purchase_loc_ph: "Ej: Tienda oficial SharkX",
    s2_help: "El número de serie ayuda a acelerar el análisis.",
    s3_title: "Datos del pedido",
    s3_text: "Necesitamos estos datos para localizar su compra y responder rápidamente.",
    s3_name: "Nombre completo", s3_name_ph: "Su nombre completo",
    s3_email: "Email", s3_email_ph: "su@email.com",
    s3_phone: "Teléfono / WhatsApp", s3_phone_ph: "+34 600 000 000",
    s3_order: "Número de pedido", s3_order_ph: "Opcional",
    s3_country: "País / Región", s3_country_ph: "Ej: España",
    s3_city: "Ciudad", s3_city_ph: "Ej: Madrid",
    s3_channel: "Canal de compra",
    s3_channel_opts: ["Web", "Tienda", "Revendedor", "Otro"],
    s3_help: "Si no tiene el número de pedido, aún puede continuar y adjuntar el comprobante después.",
    s4_title: "Describa el problema",
    s4_text: "Explique lo que pasó de la forma más objetiva posible.",
    s4_category: "Categoría del problema",
    s4_cat_opts: ["No enciende", "Batería", "Control", "Estructura", "Cargador", "Ruido", "Vibración", "Daño físico", "Otro"],
    s4_desc: "Describa lo que pasó", s4_desc_ph: "Sea lo más específico posible...",
    s4_when: "¿Cuándo empezó el problema?",
    s4_context: "¿Cuándo ocurrió?",
    s4_ctx_opts: ["Durante el uso", "Transporte", "Almacenamiento", "Carga", "Otro"],
    s4_partial: "¿El producto aún funciona parcialmente?",
    s4_partial_yes: "Sí, parcialmente", s4_partial_no: "No", s4_partial_na: "No estoy seguro",
    s4_frequency: "¿El problema es intermitente o constante?",
    s4_freq_int: "Intermitente", s4_freq_const: "Constante",
    s4_example1: "Ejemplo: el control no conecta después de la última carga.",
    s4_example2: "Ejemplo: la batería no carga y el LED parpadea.",
    s5_title: "Adjunte evidencias",
    s5_text: "Las imágenes y videos ayudan a acelerar la evaluación.",
    s5_product: "Foto del producto",
    s5_serial: "Foto del número de serie",
    s5_defect: "Foto del defecto",
    s5_video: "Video del problema",
    s5_receipt: "Comprobante de compra",
    s5_other: "Otros archivos relevantes",
    s5_formats: "Aceptados: JPG, PNG, MP4, PDF. Máx 20 MB por archivo.",
    s5_help: "Envíe imágenes claras del defecto, del número de serie y del comprobante de compra.",
    s5_drop: "Haga clic o arrastre archivos aquí",
    s5_uploading: "Enviando...",
    s5_remove: "Eliminar",
    s6_title: "Revise antes de enviar",
    s6_text: "Revise la información antes de finalizar. Después del envío, recibirá un número de protocolo para seguir su caso.",
    s6_product: "Producto", s6_serial: "Número de serie", s6_contact: "Contacto",
    s6_issue: "Descripción del problema", s6_attachments: "Adjuntos", s6_notes: "Observaciones",
    s6_terms: "Declaro que la información enviada es verdadera y acepto la política de garantía y privacidad aplicable.",
    s6_edit: "Editar", s6_send: "Enviar solicitud",
    s7_title: "Solicitud enviada con éxito",
    s7_text: "Su solicitud fue creada. Guarde este número de protocolo para seguir la atención.",
    s7_protocol: "Número de protocolo",
    s7_status: "Estado actual", s7_status_val: "Recibido",
    s7_summary: "Resumen de la solicitud",
    s7_eta: "Plazo estimado de respuesta", s7_eta_val: "2-5 días hábiles",
    s7_channel: "Canal de seguimiento",
    s7_view: "Ver solicitud", s7_support: "Volver a soporte", s7_faq: "Explorar FAQ",
    s7_secondary: "Nuestro equipo revisará la información y responderá por el canal indicado.",
    t_title: "Seguimiento de solicitud de garantía",
    t_not_found: "Solicitud no encontrada. Verifique la URL o contacte soporte.",
    t_timeline: "Línea de tiempo", t_messages: "Mensajes", t_documents: "Documentos pendientes",
    t_eta: "Plazo estimado", t_updated: "Última actualización",
    t_status_opts: ["Recibido", "En análisis", "Esperando documentos", "Evaluación técnica", "Aprobado", "Rechazado", "Completado", "Cancelado"],
    t_add_doc: "Enviar documento adicional", t_reply: "Responder al ticket", t_talk: "Hablar con soporte",
    t_support_msg: "Si falta alguna información, puede complementar la solicitud desde esta pantalla.",
  },
};

// Generate a protocol number
function generateProtocol() {
  const ts = Date.now().toString(36).toUpperCase();
  const rnd = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `WR-${ts}-${rnd}`;
}

// File upload simulation
function simulateUpload(file, onProgress) {
  return new Promise((resolve) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30 + 10;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        resolve({ name: file.name, size: file.size, type: file.type, url: URL.createObjectURL(file) });
      }
      onProgress(Math.min(Math.round(progress), 100));
    }, 200);
  });
}

export default function WarrantyRequest() {
  const locale = detectLocale();
  const t = wData[locale] || wData.en;
  const [currentStep, setCurrentStep] = useState(0);
  const totalSteps = t.steps.length;
  const fileInputRef = useRef(null);

  // Form state
  const [form, setForm] = useState({
    // Step 1
    q1: "", q2: "", q3: "",
    // Step 2
    productType: "", model: "", serial: "", variant: "", purchaseDate: "", purchaseLoc: "",
    // Step 3
    name: "", email: "", phone: "", order: "", country: "", city: "", channel: "",
    // Step 4
    category: "", desc: "", when: "", context: "", partial: "", frequency: "",
    // Step 6
    terms: false,
  });

  // Upload state
  const [uploads, setUploads] = useState({
    product: [], serial: [], defect: [], video: [], receipt: [], other: [],
  });
  const [uploading, setUploading] = useState({});

  // Confirmation state
  const [protocol, setProtocol] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const goNext = () => { if (currentStep < totalSteps - 1) setCurrentStep((s) => s + 1); };
  const goBack = () => { if (currentStep > 0) setCurrentStep((s) => s - 1); };
  const goToStep = (s) => { if (s >= 0 && s < totalSteps) setCurrentStep(s); };

  const progressPercent = ((currentStep + 1) / totalSteps) * 100;

  // Check if any answer in step 1 is "not sure"
  const hasDoubt = form.q1 === "not_sure" || form.q2 === "not_sure" || form.q3 === "not_sure";

  // Handle file upload
  const handleFileUpload = async (category, files) => {
    for (const file of files) {
      const id = `${category}_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
      setUploading((prev) => ({ ...prev, [id]: 0 }));
      const result = await simulateUpload(file, (p) => {
        setUploading((prev) => ({ ...prev, [id]: p }));
      });
      setUploads((prev) => ({
        ...prev,
        [category]: [...prev[category], { id, ...result, progress: 100 }],
      }));
      setUploading((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const removeFile = (category, fileId) => {
    setUploads((prev) => ({
      ...prev,
      [category]: prev[category].filter((f) => f.id !== fileId),
    }));
  };

  const totalUploaded = Object.values(uploads).flat().length;

  // Submit
  const handleSubmit = () => {
    const proto = generateProtocol();
    setProtocol(proto);
    setSubmitted(true);
    setCurrentStep(6); // Go to confirmation step
    // Save to localStorage for tracking
    const requestData = {
      protocol: proto,
      form,
      uploads: Object.fromEntries(
        Object.entries(uploads).map(([k, v]) => [k, v.map((f) => ({ name: f.name, size: f.size }))])
      ),
      status: 0,
      createdAt: new Date().toISOString(),
      timeline: [
        { status: 0, date: new Date().toISOString(), text: t.s7_status_val },
      ],
    };
    const existing = JSON.parse(localStorage.getItem("sharkx_warranty_requests") || "[]");
    existing.push(requestData);
    localStorage.setItem("sharkx_warranty_requests", JSON.stringify(existing));
  };

  const inputCls = "w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors placeholder:text-slate-400";
  const labelCls = "block text-sm font-medium text-slate-700 mb-1.5";
  const selectCls = "w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors appearance-none cursor-pointer";

  return (
    <main className="pt-32 pb-32 bg-[#f6f6f3] min-h-screen" data-testid="warranty-request-page">
      <div className="max-w-[720px] mx-auto px-6 md:px-12">

        {/* Progress Bar */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-ui uppercase tracking-[0.15em] text-slate-500">
              {t.stepLabel} {currentStep + 1} {t.of} {totalSteps}
            </span>
            <span className="text-xs font-ui text-slate-400">{t.steps[currentStep]}</span>
          </div>
          <div className="w-full h-1 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-sky-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex justify-between mt-3">
            {t.steps.map((step, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors duration-300 ${
                  i < currentStep ? "bg-sky-500 text-white" : i === currentStep ? "bg-sky-500 text-white ring-4 ring-sky-100" : "bg-slate-200 text-slate-400"
                }`}>
                  {i < currentStep ? <CheckCircle size={14} /> : i + 1}
                </div>
                <span className={`text-[10px] mt-1 font-ui text-center ${i <= currentStep ? "text-slate-700" : "text-slate-400"}`}>
                  {step}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white border border-slate-200 rounded-lg p-8 md:p-12 mb-8">

          {/* ===== STEP 0: Landing ===== */}
          {currentStep === 0 && (
            <div>
              <div className="overline mb-4">{t.overline}</div>
              <h1 className="font-serif-display text-3xl md:text-4xl text-slate-900 mb-4">{t.title}</h1>
              <p className="text-slate-600 text-base leading-relaxed mb-6">{t.subtitle}</p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                <p className="text-slate-500 text-sm leading-relaxed">{t.helpText}</p>
              </div>
              <ul className="space-y-3 mb-8">
                {t.checklist.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle className="text-sky-500 flex-shrink-0" size={18} />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={goNext}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                  {t.ctaPrimary} <ChevronRight size={16} />
                </button>
                <a href="/warranty"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                  <Shield size={14} /> {t.ctaSecondary}
                </a>
              </div>
            </div>
          )}

          {/* ===== STEP 1: Initial Check ===== */}
          {currentStep === 1 && (
            <div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-2">{t.s1_title}</h2>
              <p className="text-slate-500 text-sm mb-8">{t.s1_text}</p>

              {[
                { key: "q1", label: t.s1_q1, a1: t.s1_q1_a1, a2: t.s1_q1_a2 },
                { key: "q2", label: t.s1_q2, a1: t.s1_q2_a1, a2: t.s1_q2_a2 },
                { key: "q3", label: t.s1_q3, a1: t.s1_q3_a1, a2: t.s1_q3_a2 },
              ].map((q) => (
                <div key={q.key} className="mb-6">
                  <p className="text-sm font-medium text-slate-700 mb-3">{q.label}</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => updateForm(q.key, "yes")}
                      className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-colors ${
                        form[q.key] === "yes"
                          ? "border-sky-500 bg-sky-50 text-sky-700"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {q.a1}
                    </button>
                    <button
                      onClick={() => updateForm(q.key, "not_sure")}
                      className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-colors ${
                        form[q.key] === "not_sure"
                          ? "border-amber-500 bg-amber-50 text-amber-700"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {q.a2}
                    </button>
                  </div>
                </div>
              ))}

              {hasDoubt && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-amber-700">{t.s1_manual}</p>
                </div>
              )}
            </div>
          )}

          {/* ===== STEP 2: Product Identification ===== */}
          {currentStep === 2 && (
            <div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-2">{t.s2_title}</h2>
              <p className="text-slate-500 text-sm mb-8">{t.s2_text}</p>

              <div className="space-y-4">
                <div>
                  <label className={labelCls}>{t.s2_type}</label>
                  <select className={selectCls} value={form.productType} onChange={(e) => updateForm("productType", e.target.value)}>
                    <option value="">{t.s2_type_ph}</option>
                    {t.s2_type_opts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>{t.s2_model}</label>
                    <input className={inputCls} placeholder={t.s2_model_ph} value={form.model} onChange={(e) => updateForm("model", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>{t.s2_serial}</label>
                    <input className={inputCls} placeholder={t.s2_serial_ph} value={form.serial} onChange={(e) => updateForm("serial", e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{t.s2_variant}</label>
                  <input className={inputCls} placeholder={t.s2_variant_ph} value={form.variant} onChange={(e) => updateForm("variant", e.target.value)} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>{t.s2_purchase_date}</label>
                    <input type="date" className={inputCls} value={form.purchaseDate} onChange={(e) => updateForm("purchaseDate", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>{t.s2_purchase_loc}</label>
                    <input className={inputCls} placeholder={t.s2_purchase_loc_ph} value={form.purchaseLoc} onChange={(e) => updateForm("purchaseLoc", e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="mt-6 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-2">
                <HelpCircle className="text-slate-400 flex-shrink-0 mt-0.5" size={16} />
                <p className="text-xs text-slate-500">{t.s2_help}</p>
              </div>
            </div>
          )}

          {/* ===== STEP 3: Contact / Order Data ===== */}
          {currentStep === 3 && (
            <div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-2">{t.s3_title}</h2>
              <p className="text-slate-500 text-sm mb-8">{t.s3_text}</p>

              <div className="space-y-4">
                <div>
                  <label className={labelCls}>{t.s3_name}</label>
                  <input className={inputCls} placeholder={t.s3_name_ph} value={form.name} onChange={(e) => updateForm("name", e.target.value)} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>{t.s3_email}</label>
                    <input type="email" className={inputCls} placeholder={t.s3_email_ph} value={form.email} onChange={(e) => updateForm("email", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>{t.s3_phone}</label>
                    <input className={inputCls} placeholder={t.s3_phone_ph} value={form.phone} onChange={(e) => updateForm("phone", e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>{t.s3_order}</label>
                    <input className={inputCls} placeholder={t.s3_order_ph} value={form.order} onChange={(e) => updateForm("order", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>{t.s3_channel}</label>
                    <select className={selectCls} value={form.channel} onChange={(e) => updateForm("channel", e.target.value)}>
                      <option value="">{locale === "pt" ? "Selecione" : locale === "es" ? "Seleccione" : "Select"}</option>
                      {t.s3_channel_opts.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>{t.s3_country}</label>
                    <input className={inputCls} placeholder={t.s3_country_ph} value={form.country} onChange={(e) => updateForm("country", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>{t.s3_city}</label>
                    <input className={inputCls} placeholder={t.s3_city_ph} value={form.city} onChange={(e) => updateForm("city", e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="mt-6 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-2">
                <HelpCircle className="text-slate-400 flex-shrink-0 mt-0.5" size={16} />
                <p className="text-xs text-slate-500">{t.s3_help}</p>
              </div>
            </div>
          )}

          {/* ===== STEP 4: Describe the Issue ===== */}
          {currentStep === 4 && (
            <div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-2">{t.s4_title}</h2>
              <p className="text-slate-500 text-sm mb-8">{t.s4_text}</p>

              <div className="space-y-4">
                <div>
                  <label className={labelCls}>{t.s4_category}</label>
                  <select className={selectCls} value={form.category} onChange={(e) => updateForm("category", e.target.value)}>
                    <option value="">{locale === "pt" ? "Selecione" : locale === "es" ? "Seleccione" : "Select"}</option>
                    {t.s4_cat_opts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t.s4_desc}</label>
                  <textarea className={`${inputCls} min-h-[100px] resize-y`} placeholder={t.s4_desc_ph} value={form.desc} onChange={(e) => updateForm("desc", e.target.value)} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>{t.s4_when}</label>
                    <input type="date" className={inputCls} value={form.when} onChange={(e) => updateForm("when", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>{t.s4_context}</label>
                    <select className={selectCls} value={form.context} onChange={(e) => updateForm("context", e.target.value)}>
                      <option value="">{locale === "pt" ? "Selecione" : locale === "es" ? "Seleccione" : "Select"}</option>
                      {t.s4_ctx_opts.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{t.s4_partial}</label>
                  <div className="flex gap-3">
                    {[["yes", t.s4_partial_yes], ["no", t.s4_partial_no], ["na", t.s4_partial_na]].map(([v, l]) => (
                      <button key={v} onClick={() => updateForm("partial", v)}
                        className={`flex-1 py-2.5 px-3 rounded-lg border text-sm font-medium transition-colors ${
                          form.partial === v ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}>{l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{t.s4_frequency}</label>
                  <div className="flex gap-3">
                    {[["intermittent", t.s4_freq_int], ["constant", t.s4_freq_const]].map(([v, l]) => (
                      <button key={v} onClick={() => updateForm("frequency", v)}
                        className={`flex-1 py-2.5 px-3 rounded-lg border text-sm font-medium transition-colors ${
                          form.frequency === v ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">{t.s4_example1}</p>
                <p className="text-xs text-slate-500">{t.s4_example2}</p>
              </div>
            </div>
          )}

          {/* ===== STEP 5: Upload Evidence ===== */}
          {currentStep === 5 && (
            <div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-2">{t.s5_title}</h2>
              <p className="text-slate-500 text-sm mb-8">{t.s5_text}</p>

              <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,.pdf" className="hidden"
                onChange={(e) => { handleFileUpload("other", Array.from(e.target.files || [])); e.target.value = ""; }} />

              {[
                { key: "product", label: t.s5_product },
                { key: "serial", label: t.s5_serial },
                { key: "defect", label: t.s5_defect },
                { key: "video", label: t.s5_video },
                { key: "receipt", label: t.s5_receipt },
                { key: "other", label: t.s5_other },
              ].map((cat) => (
                <div key={cat.key} className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">{cat.label}</label>
                    <button
                      onClick={() => {
                        const input = document.createElement("input");
                        input.type = "file";
                        input.multiple = true;
                        input.accept = "image/*,video/*,.pdf";
                        input.onchange = (e) => {
                          handleFileUpload(cat.key, Array.from(e.target.files || []));
                        };
                        input.click();
                      }}
                      className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700 font-medium"
                    >
                      <Plus size={14} /> {locale === "pt" ? "Adicionar" : locale === "es" ? "Añadir" : "Add"}
                    </button>
                  </div>
                  {uploads[cat.key].length > 0 && (
                    <div className="space-y-2">
                      {uploads[cat.key].map((file) => (
                        <div key={file.id} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                          <Paperclip size={14} className="text-slate-400 flex-shrink-0" />
                          <span className="text-sm text-slate-700 flex-1 truncate">{file.name}</span>
                          <span className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)} KB</span>
                          <button onClick={() => removeFile(cat.key, file.id)} className="text-slate-400 hover:text-red-500 transition-colors">
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <div className="mt-6 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-xs text-slate-500">{t.s5_formats}</p>
                <p className="text-xs text-slate-500 mt-1">{t.s5_help}</p>
              </div>
            </div>
          )}

          {/* ===== STEP 6: Review ===== */}
          {currentStep === 6 && !submitted && (
            <div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-2">{t.s6_title}</h2>
              <p className="text-slate-500 text-sm mb-8">{t.s6_text}</p>

              <div className="space-y-4">
                {/* Product summary */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">{t.s6_product}</h3>
                    <button onClick={() => goToStep(2)} className="text-xs text-sky-600 hover:text-sky-700 flex items-center gap-1"><Edit3 size={12} /> {t.s6_edit}</button>
                  </div>
                  <div className="grid grid-cols-1 gap-1 text-sm">
                    <div><span className="text-slate-500">{t.s2_type}:</span> <span className="text-slate-700">{form.productType || "—"}</span></div>
                    <div><span className="text-slate-500">{t.s2_model}:</span> <span className="text-slate-700">{form.model || "—"}</span></div>
                    <div><span className="text-slate-500">{t.s6_serial}:</span> <span className="text-slate-700">{form.serial || "—"}</span></div>
                    <div><span className="text-slate-500">{t.s2_variant}:</span> <span className="text-slate-700">{form.variant || "—"}</span></div>
                  </div>
                </div>

                {/* Contact summary */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">{t.s6_contact}</h3>
                    <button onClick={() => goToStep(3)} className="text-xs text-sky-600 hover:text-sky-700 flex items-center gap-1"><Edit3 size={12} /> {t.s6_edit}</button>
                  </div>
                  <div className="grid grid-cols-1 gap-1 text-sm">
                    <div><span className="text-slate-500">{t.s3_name}:</span> <span className="text-slate-700">{form.name || "—"}</span></div>
                    <div><span className="text-slate-500">{t.s3_email}:</span> <span className="text-slate-700">{form.email || "—"}</span></div>
                    <div><span className="text-slate-500">{t.s3_phone}:</span> <span className="text-slate-700">{form.phone || "—"}</span></div>
                  </div>
                </div>

                {/* Issue summary */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">{t.s6_issue}</h3>
                    <button onClick={() => goToStep(4)} className="text-xs text-sky-600 hover:text-sky-700 flex items-center gap-1"><Edit3 size={12} /> {t.s6_edit}</button>
                  </div>
                  <div className="grid grid-cols-1 gap-1 text-sm">
                    <div><span className="text-slate-500">{t.s4_category}:</span> <span className="text-slate-700">{form.category || "—"}</span></div>
                    <div><span className="text-slate-500">{t.s4_desc}:</span> <span className="text-slate-700">{form.desc || "—"}</span></div>
                  </div>
                </div>

                {/* Attachments summary */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">{t.s6_attachments}</h3>
                    <button onClick={() => goToStep(5)} className="text-xs text-sky-600 hover:text-sky-700 flex items-center gap-1"><Edit3 size={12} /> {t.s6_edit}</button>
                  </div>
                  <p className="text-sm text-slate-700">
                    {totalUploaded > 0
                      ? `${totalUploaded} ${locale === "pt" ? "arquivo(s) anexado(s)" : locale === "es" ? "archivo(s) adjunto(s)" : "file(s) attached"}`
                      : locale === "pt" ? "Nenhum anexo" : locale === "es" ? "Sin adjuntos" : "No attachments"}
                  </p>
                </div>

                {/* Terms */}
                <div className="pt-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.terms} onChange={(e) => updateForm("terms", e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-slate-300 text-sky-500 focus:ring-sky-500" />
                    <span className="text-sm text-slate-600 leading-relaxed">{t.s6_terms}</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* ===== STEP 7: Confirmation ===== */}
          {currentStep === 6 && submitted && protocol && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center mx-auto mb-6">
                <Check size={32} />
              </div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-slate-900 mb-3">{t.s7_title}</h2>
              <p className="text-slate-500 text-sm mb-8 max-w-md mx-auto">{t.s7_text}</p>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-8 text-left space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">{t.s7_protocol}</span>
                  <span className="text-sm font-mono font-bold text-slate-900 bg-white px-3 py-1 rounded border border-slate-200">{protocol}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">{t.s7_status}</span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 bg-sky-50 px-3 py-1 rounded-full">
                    <span className="w-2 h-2 rounded-full bg-sky-500" /> {t.s7_status_val}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">{t.s7_eta}</span>
                  <span className="text-sm text-slate-700">{t.s7_eta_val}</span>
                </div>
                <hr className="border-slate-200" />
                <div>
                  <span className="text-sm text-slate-500">{t.s7_summary}</span>
                  <p className="text-sm text-slate-700 mt-1">
                    {form.productType} {form.model} — {form.category}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href={`/warranty/request/${protocol}`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                  <Eye size={16} /> {t.s7_view}
                </a>
                <a href="/support"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                  <ArrowLeft size={16} /> {t.s7_support}
                </a>
                <a href="/faq"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                  <ExternalLink size={16} /> {t.s7_faq}
                </a>
              </div>

              <p className="text-sm text-slate-500 mt-6 max-w-md mx-auto">{t.s7_secondary}</p>
            </div>
          )}
        </div>

        {/* Wizard Navigation */}
        {!(currentStep === 6 && submitted) && (
          <div className="flex items-center justify-between">
            <button
              onClick={goBack}
              disabled={currentStep === 0}
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-ui font-semibold uppercase tracking-wider transition-colors ${
                currentStep === 0
                  ? "text-slate-300 cursor-not-allowed"
                  : "text-slate-600 hover:text-slate-900 border border-slate-300 hover:border-slate-400"
              }`}
            >
              <ChevronLeft size={16} /> {t.back}
            </button>

            {currentStep < 5 ? (
              <button
                onClick={goNext}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors"
              >
                {t.next} <ChevronRight size={16} />
              </button>
            ) : currentStep === 5 ? (
              <button
                onClick={goNext}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors"
              >
                {t.next} <ChevronRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!form.terms}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-ui font-semibold uppercase tracking-wider transition-colors ${
                  form.terms
                    ? "bg-sky-500 hover:bg-sky-600 text-white"
                    : "bg-slate-200 text-slate-400 cursor-not-allowed"
                }`}
              >
                {t.submit}
              </button>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
